import { VIDEO_WATCHDOG_GRACE_MS } from '../constants.js';
import type { Logger } from '../log.js';
import type { Timer } from '../timer.js';
import type {
  CardPresentation,
  Presentation,
  PresentationOutcome,
  Renderer,
} from './presentation.js';
import { DEFAULT_BACKGROUND_COLOR } from './scaling.js';

/**
 * The WebView renderer: one `<video>`, one `<img>`, one card layer, reused for the
 * life of the process. Nothing here decides what plays; it draws what the state
 * machine hands it and reports how it ended.
 */

export interface DomRendererOptions {
  readonly root: HTMLElement;
  readonly document: Document;
  readonly timer: Timer;
  readonly logger: Logger;
  /** Injected so the harness can avoid Blob/URL support questions. */
  readonly createObjectUrl?: (bytes: Uint8Array, mediaType: string) => string;
  readonly revokeObjectUrl?: (url: string) => void;
}

const OBJECT_FIT = { fit: 'contain', fill: 'cover', stretch: 'fill' } as const;

export function createDomRenderer(options: DomRendererOptions): Renderer {
  const doc = options.document;
  const stage = doc.createElement('div');
  stage.className = 'dw-stage';
  const video = doc.createElement('video');
  video.className = 'dw-video';
  video.setAttribute('playsinline', '');
  video.muted = true;
  const image = doc.createElement('img');
  image.className = 'dw-image';
  image.alt = '';
  const card = doc.createElement('div');
  card.className = 'dw-card';
  stage.append(video, image, card);
  options.root.append(stage);

  const createObjectUrl =
    options.createObjectUrl ??
    ((bytes, mediaType) => {
      const copy = new Uint8Array(bytes.byteLength);
      copy.set(bytes);
      return URL.createObjectURL(new Blob([copy], { type: mediaType }));
    });
  const revokeObjectUrl = options.revokeObjectUrl ?? ((url) => URL.revokeObjectURL(url));

  let cancelCurrent: (() => void) | null = null;

  const showOnly = (element: HTMLElement | null): void => {
    for (const candidate of [video, image, card]) {
      candidate.style.display = candidate === element ? 'block' : 'none';
    }
  };

  const present = (presentation: Presentation): Promise<PresentationOutcome> =>
    new Promise<PresentationOutcome>((resolve) => {
      let settled = false;
      const finish = (outcome: PresentationOutcome): void => {
        if (settled) return;
        settled = true;
        cancelCurrent = null;
        resolve(outcome);
      };

      if (presentation.kind === 'card') {
        renderCard(doc, card, presentation);
        stage.style.background = DEFAULT_BACKGROUND_COLOR;
        showOnly(card);
        const cancelHold = options.timer.after(presentation.hold_ms, () => {
          finish({ completed: true, reason: 'timer' });
        });
        cancelCurrent = () => {
          cancelHold();
          finish({ completed: false, reason: 'stopped' });
        };
        return;
      }

      if (presentation.kind === 'house' && presentation.asset === null) {
        renderHouseCard(doc, card);
        stage.style.background = DEFAULT_BACKGROUND_COLOR;
        showOnly(card);
        const cancelHold = options.timer.after(presentation.duration_ms, () => {
          finish({ completed: true, reason: 'timer' });
        });
        cancelCurrent = () => {
          cancelHold();
          finish({ completed: false, reason: 'stopped' });
        };
        return;
      }

      const asset = presentation.asset;
      if (asset === null) {
        finish({ completed: false, reason: 'error' });
        return;
      }

      const scaling = presentation.kind === 'asset' ? presentation.scaling : 'fit';
      stage.style.background =
        presentation.kind === 'asset' ? presentation.background_color : DEFAULT_BACKGROUND_COLOR;

      let url: string;
      try {
        url = createObjectUrl(asset.bytes, asset.media_type);
      } catch (error) {
        options.logger.error('renderer.object_url_failed', {
          media_type: asset.media_type,
          reason: error instanceof Error ? error.message : String(error),
        });
        finish({ completed: false, reason: 'error' });
        return;
      }

      const release = (): void => {
        try {
          revokeObjectUrl(url);
        } catch {
          // A WebView that cannot revoke is not a reason to stop playing.
        }
      };

      if (asset.media_type.startsWith('video/')) {
        video.style.objectFit = OBJECT_FIT[scaling];
        video.muted = presentation.kind === 'asset' ? presentation.muted : true;
        video.src = url;
        showOnly(video);

        const cancelWatchdog = options.timer.after(
          presentation.duration_ms + VIDEO_WATCHDOG_GRACE_MS,
          () => {
            options.logger.warn('renderer.video_watchdog', {
              duration_ms: presentation.duration_ms,
            });
            cleanup();
            finish({ completed: false, reason: 'watchdog' });
          },
        );

        const onEnded = (): void => {
          const width = video.videoWidth;
          const height = video.videoHeight;
          cleanup();
          finish({
            completed: true,
            reason: 'ended',
            ...(width > 0 ? { source_width: width, source_height: height } : {}),
          });
        };
        const onError = (): void => {
          cleanup();
          finish({ completed: false, reason: 'error' });
        };

        function cleanup(): void {
          cancelWatchdog();
          video.removeEventListener('ended', onEnded);
          video.removeEventListener('error', onError);
          release();
        }

        video.addEventListener('ended', onEnded);
        video.addEventListener('error', onError);
        void Promise.resolve(video.play?.()).catch((error: unknown) => {
          options.logger.warn('renderer.video_play_rejected', {
            reason: error instanceof Error ? error.message : String(error),
          });
        });

        cancelCurrent = () => {
          cleanup();
          finish({ completed: false, reason: 'stopped' });
        };
        return;
      }

      image.style.objectFit = OBJECT_FIT[scaling];
      image.src = url;
      showOnly(image);

      // An image runs for exactly `duration_ms` (optisigns-parity §1), counted from
      // the moment it is presented rather than from `load`: a WebView that never
      // fires `load` for an object URL must not stall the loop.
      const cancelHold = options.timer.after(presentation.duration_ms, () => {
        const width = image.naturalWidth;
        const height = image.naturalHeight;
        cleanupImage();
        finish({
          completed: true,
          reason: 'timer',
          ...(width > 0 ? { source_width: width, source_height: height } : {}),
        });
      });
      const onError = (): void => {
        cleanupImage();
        finish({ completed: false, reason: 'error' });
      };
      function cleanupImage(): void {
        cancelHold();
        image.removeEventListener('error', onError);
        release();
      }
      image.addEventListener('error', onError);

      cancelCurrent = () => {
        cleanupImage();
        finish({ completed: false, reason: 'stopped' });
      };
    });

  return {
    present,
    viewport: () => ({
      width: options.root.clientWidth || 1920,
      height: options.root.clientHeight || 1080,
    }),
    stop: () => {
      cancelCurrent?.();
      cancelCurrent = null;
    },
  };
}

function renderCard(doc: Document, host: HTMLElement, presentation: CardPresentation): void {
  host.textContent = '';
  const wrap = doc.createElement('div');
  wrap.className = `dw-card__body dw-card__body--${presentation.card}`;

  const brand = doc.createElement('p');
  brand.className = 'dw-card__brand';
  brand.textContent = 'WaveCast';
  wrap.append(brand);

  const headline = doc.createElement('h1');
  headline.className = 'dw-card__headline';
  headline.textContent =
    presentation.card === 'pairing' ? 'Ready to activate' : 'This screen is not activated';
  wrap.append(headline);

  const qr = presentation.pairing_code === null ? null : presentation.pairing_qr_svg;

  if (presentation.pairing_code !== null) {
    const code = doc.createElement('p');
    code.className = 'dw-card__code';
    code.textContent = presentation.pairing_code;

    if (qr === null) {
      wrap.append(code);
    } else {
      // D251: the code and its QR side by side, so staff can scan rather than type. The SVG
      // comes from our own API and goes in as an image, never as markup, so nothing in it can
      // run on the TV.
      const pair = doc.createElement('div');
      pair.className = 'dw-card__pair';
      const image = doc.createElement('img');
      image.className = 'dw-card__qr';
      image.alt = '';
      image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qr)}`;
      pair.append(code, image);
      wrap.append(pair);
    }
  }

  const help = doc.createElement('p');
  help.className = 'dw-card__help';
  help.textContent =
    presentation.card !== 'pairing'
      ? 'Contact WaveCast to activate this screen.'
      : qr === null
        ? 'Type this code in WaveCast to connect this TV.'
        : 'Scan the code with your phone, or type it in WaveCast, to connect this TV.';
  wrap.append(help);

  host.append(wrap);
}

/**
 * The built-in house still. decision-log O131 (what house content is and who
 * supplies it) is open, so the fallback is a branded card the bundle carries: no
 * network, no cache, and never a black screen.
 */
function renderHouseCard(doc: Document, host: HTMLElement): void {
  host.textContent = '';
  const wrap = doc.createElement('div');
  wrap.className = 'dw-card__body dw-card__body--house';
  const brand = doc.createElement('p');
  brand.className = 'dw-card__brand';
  brand.textContent = 'WaveCast';
  const headline = doc.createElement('h1');
  headline.className = 'dw-card__headline';
  headline.textContent = 'Advertise on this screen';
  wrap.append(brand, headline);
  host.append(wrap);
}
